import { NewProfileDataResponse, NewProfileResponse, UpdateBankApiPayload, UpdateContactPreferencesApiPayload, UpdateSocialSecurityApiPayload } from "@core/app/types/v2/newProfile"
import { UpdateDetailsApiPayload } from "@page/v2/employee-profile/layout/UpdateData/Details/types"
import { UpdateLocationApiPayload } from "@page/v2/employee-profile/layout/UpdateData/Location/types"
import { UpdateProfileApiPayload } from "@page/v2/employee-profile/layout/UpdateData/Profile/types"
import { FileType } from "@page/v2/employee-profile/types"
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

interface ConnectExternalAuthMethodResponse {
    data: {
        externalAuth: {
            isValid: boolean
            message?: string
        }
    }
}

interface UpdateAvatarResponse {
    statusCode: number
    success: boolean
    message: string
    data: string
}

export const newProfileApi = createApi({
    reducerPath: 'newProfileApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.ENV === 'local' ? `${process.env.API_URL}/api` : `${window.location.origin}/api`,
        prepareHeaders: (headers) => {
            headers.set('Response-V2', '1')
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)

            const customCorporation = document.getElementById('__custom_route_data')?.getAttribute('data-corpId')
            if (customCorporation) headers.set('X-CORP-CONTEXT', customCorporation)

            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    tagTypes: ['Profile'],
    endpoints: (builder) => ({
        updateNoFields: builder.mutation<
            void,
            {
                noCourse?: boolean
                noEducation?: boolean
                noExperience?: boolean
                noProjectExperience?: boolean
                noReference?: boolean
            }
        >({
            query: (data) => ({
                url: '/profile/v2/no-fields',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        getProfileData: builder.query<NewProfileDataResponse, { fields: string }>({
            query: ({ fields }) => ({
                url: '/profile/v2',
                method: 'GET',
                params: {
                    fields,
                },
            }),
            transformResponse: (response: NewProfileResponse): NewProfileDataResponse => response.data,
            providesTags: ['Profile'],
        }),
        acceptTerms: builder.mutation<void, void>({
            query: () => ({
                url: '/profile/terms/accept',
                method: 'POST',
            }),
            invalidatesTags: ['Profile'],
        }),
        updateAvatar: builder.mutation<UpdateAvatarResponse, { file: FileType }>({
            query: ({ file }) => ({
                url: '/profile/v2/avatar',
                method: 'POST',
                body: file,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteAvatar: builder.mutation<void, void>({
            query: () => ({
                url: '/profile/v2/avatar/delete',
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),
        updateBaseProfileData: builder.mutation<void, { data: UpdateProfileApiPayload }>({
            query: ({ data }) => ({
                url: '/profile/v2/update/base',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateLocationData: builder.mutation<void, { data: UpdateLocationApiPayload }>({
            query: ({ data }) => ({
                url: '/profile/v2/update/address',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateEmploymentData: builder.mutation<void, { data: { employmentStatus: number, availableForWork: number } }>({
            query: ({ data }) => ({
                url: '/profile/v2/update/employment',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateDetailsData: builder.mutation<void, { data: UpdateDetailsApiPayload }>({
            query: ({ data }) => ({
                url: '/profile/v2/update/details',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateLanguage: builder.mutation<void, { languageId: number }>({
            query: ({ languageId }) => ({
                url: '/profile/v2/locale/update',
                method: 'PUT',
                body: { languageId },
            }),
            invalidatesTags: ['Profile'],
        }),
        updateBankData: builder.mutation<void, { data: UpdateBankApiPayload }>({
            query: ({ data }) => ({
                url: '/profile/v2/update/bank',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateSocialSecurityData: builder.mutation<void, { data: UpdateSocialSecurityApiPayload }>({
            query: ({ data }) => ({
                url: '/profile/v2/update/id',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateContactPreferences: builder.mutation<void, { data: UpdateContactPreferencesApiPayload }>({
            query: ({ data }) => ({
                url: '/profile/v2/contact/update',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        changePassword: builder.mutation<void, { currentPassword: string; newPassword: string }>({
            query: ({ currentPassword, newPassword }) => ({
                url: '/profile/v2/password/update',
                method: 'PUT',
                body: { currentPassword, newPassword },
            }),
            invalidatesTags: ['Profile', 'Employee'],
        }),
        requestProfileDeletion: builder.mutation<void, void>({
            query: () => ({
                url: '/profile/v2/deletion-request',
                method: 'POST',
            }),
            invalidatesTags: ['Profile'],
        }),
        withdrawProfileDeletion: builder.mutation<void, void>({
            query: () => ({
                url: '/profile/v2/deletion-request/cancel',
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),
        postSendSmsCode: builder.mutation<void, { mobilePhone: {number: string; prefix: string}, ['g-recaptcha-response']: string }>({
            query: (body) => ({
                url: '/profile/v2/auth/send-sms',
                method: 'POST',
                body
            }),
        }),
        verifySmsCode: builder.mutation<void, { code: string }>({
            query: ({ code }) => ({
                url: '/profile/v2/auth/validate-sms',
                method: 'POST',
                body: { code },
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteAuthMethod: builder.mutation<void, { method: number }>({
            query: ({ method }) => ({
                url: `/profile/v2/auth/delete/${method}`,
                method: 'POST',
            }),
            invalidatesTags: ['Profile'],
        }),
        connectExternalAuthMethod: builder.mutation<ConnectExternalAuthMethodResponse, { code: string }>({
            query: ({ code }) => ({
                url: `/profile/v2/auth/connect${code}`,
                method: 'GET',
            }),
            invalidatesTags: ['Profile'],
        }),
        uploadFile: builder.mutation<void, { file: FileType }>({
            query: ({ file }) => ({
                url: '/profile/v2/file/upload',
                method: 'POST',
                body: file,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteFile: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/file/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: work experience
        createWorkExperience: builder.mutation<
            void,
            {
                data: {
                    companyName: string
                    title: string
                    location?: string | null
                    fromDate: string
                    toDate: string | null
                    description?: string | null
                    current: boolean
                }
            }
        >({
            query: ({ data }) => ({
                url: '/profile/v2/work-experience/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateWorkExperience: builder.mutation<
            void,
            {
                id: number
                data: {
                    companyName: string
                    title: string
                    location?: string | null
                    fromDate: string
                    toDate: string | null
                    description?: string | null
                    current: boolean
                }
            }
        >({
            query: ({ id, data }) => ({
                url: `/profile/v2/work-experience/${id}/update`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteWorkExperience: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/work-experience/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: education
        createEducation: builder.mutation<
            void,
            {
                data: {
                    schoolName: string
                    educationType?: string | null
                    degree?: string | null
                    location?: string | null
                    fromDate: string
                    toDate: string | null
                    description?: string | null
                    educationListId?: number | null
                }
            }
        >({
            query: ({ data }) => ({
                url: '/profile/v2/education/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateEducation: builder.mutation<
            void,
            {
                id: number
                data: {
                    schoolName: string
                    educationType?: string | null
                    degree?: string | null
                    location?: string | null
                    fromDate: string
                    toDate: string | null
                    description?: string | null
                    educationListId?: number | null
                }
            }
        >({
            query: ({ id, data }) => ({
                url: `/profile/v2/education/${id}/update`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteEducation: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/education/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: project experience
        createProjectExperience: builder.mutation<
            void,
            {
                data: {
                    name: string
                    description?: string | null
                    url?: string | null
                    role?: string | null
                    fromDate?: string | null
                    toDate?: string | null
                    current?: boolean
                }
            }
        >({
            query: ({ data }) => ({
                url: '/profile/v2/project-experience/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateProjectExperience: builder.mutation<
            void,
            {
                id: number
                data: {
                    name: string
                    description?: string | null
                    url?: string | null
                    role?: string | null
                    fromDate?: string | null
                    toDate?: string | null
                    current?: boolean
                }
            }
        >({
            query: ({ id, data }) => ({
                url: `/profile/v2/project-experience/${id}/update`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteProjectExperience: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/project-experience/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: courses / certificates
        createCourse: builder.mutation<
            void,
            {
                data: {
                    name: string
                    description: string
                    startDate?: string | null
                    endDate?: string | null
                    courseListId?: number | null
                    files?: Array<{ name: string; ext: string; base64: string }>
                }
            }
        >({
            query: ({ data }) => ({
                url: '/profile/v2/course/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateCourse: builder.mutation<
            void,
            {
                id: number
                data: {
                    name: string
                    description: string
                    startDate?: string | null
                    endDate?: string | null
                    courseListId?: number | null
                }
            }
        >({
            query: ({ id, data }) => ({
                url: `/profile/v2/course/${id}/update`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        uploadCourseFile: builder.mutation<void, { id: number; data: { name: string; ext: string; base64: string } }>({
            query: ({ id, data }) => ({
                url: `/profile/v2/course/${id}/file/upload`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteCourseFile: builder.mutation<void, { id: number; fileId: number }>({
            query: ({ id, fileId }) => ({
                url: `/profile/v2/course/${id}/file/${fileId}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteCourse: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/course/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: references
        createReference: builder.mutation<
            void,
            {
                data: {
                    name: string
                    title: string
                    companyName: string
                    description?: string | null
                    email?: string | null
                    mobilePhone?: { prefix: string; number: string } | null
                }
            }
        >({
            query: ({ data }) => ({
                url: '/profile/v2/reference/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateReference: builder.mutation<
            void,
            {
                id: number
                data: {
                    name: string
                    title: string
                    companyName: string
                    description?: string | null
                    email?: string | null
                    mobilePhone?: { prefix: string; number: string } | null
                }
            }
        >({
            query: ({ id, data }) => ({
                url: `/profile/v2/reference/${id}/update`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteReference: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/reference/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: relatives (next of kin)
        createRelative: builder.mutation<
            void,
            {
                data: {
                    name: string
                    relation: string
                    mobilePhone?: { prefix: string; number: string } | null
                    workPhone?: { prefix: string; number: string } | null
                    homePhone?: { prefix: string; number: string } | null
                    email?: string | null
                    note?: string | null
                }
            }
        >({
            query: ({ data }) => ({
                url: '/profile/v2/relative/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateRelative: builder.mutation<
            void,
            {
                id: number
                data: {
                    name: string
                    relation: string
                    mobilePhone?: { prefix: string; number: string } | null
                    workPhone?: { prefix: string; number: string } | null
                    homePhone?: { prefix: string; number: string } | null
                    email?: string | null
                    note?: string | null
                }
            }
        >({
            query: ({ id, data }) => ({
                url: `/profile/v2/relative/${id}/update`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteRelative: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/relative/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: skills
        createSkill: builder.mutation<
            void,
            {
                data: {
                    skill: string
                    rating: number
                    skillTagId?: number
                }
            }
        >({
            query: ({ data }) => ({
                url: '/profile/v2/skill/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateSkill: builder.mutation<
            void,
            {
                id: number
                data: {
                    rating: number
                }
            }
        >({
            query: ({ id, data }) => ({
                url: `/profile/v2/skill/${id}/update`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteSkill: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/skill/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),
        batchSkill: builder.mutation<
            void,
            {
                data: {
                    add?: Array<{
                        skill: string
                        rating: number
                        skillTagId?: number
                    }>
                    delete?: number[]
                }
            }
        >({
            query: ({ data }) => ({
                url: '/profile/v2/skill/batch',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: languages
        createLanguage: builder.mutation<void, { data: { worldLanguageId: number; languageLevelId: number } }>({
            query: ({ data }) => ({
                url: '/profile/v2/language/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        updateLanguageItem: builder.mutation<void, { id: number; data: { languageLevelId: number } }>({
            query: ({ id, data }) => ({
                url: `/profile/v2/language/${id}/update`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteLanguageItem: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/language/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: driver licences
        updateLicences: builder.mutation<void, { ids: number[] }>({
            query: ({ ids }) => ({
                url: '/profile/v2/licence',
                method: 'POST',
                body: { ids },
            }),
            invalidatesTags: ['Profile'],
        }),

        // CV: files
        uploadCv: builder.mutation<void, { name: string; ext: string; base64: string }>({
            query: (data) => ({
                url: '/profile/v2/cv/upload',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        deleteProfileFile: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/profile/v2/file/${id}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),
        connectCvParsingSocket: builder.mutation<
            { success: boolean; token: string; url: string; candidateId: number; subscriptionId: number },
            void
        >({
            query: () => ({
                url: '/profile/v2/connect/socket',
                method: 'GET',
            }),
            transformResponse: (response: {
                statusCode: number
                success: boolean
                message: string
                data: { success: boolean; token: string; url: string; candidateId: number; subscriptionId: number }
            }) => response.data,
        }),
        triggerCvParsing: builder.mutation<void, void>({
            query: () => ({
                url: '/profile/v2/trigger/cv',
                method: 'GET',
            }),
        }),

        // CV: about me / description
        updateDescription: builder.mutation<void, { description: string }>({
            query: ({ description }) => ({
                url: '/profile/v2/update/description',
                method: 'PUT',
                body: { description },
            }),
            invalidatesTags: ['Profile'],
        }),
    }),
})

export const {
    useUpdateNoFieldsMutation,
    useGetProfileDataQuery,
    useAcceptTermsMutation,
    useUpdateAvatarMutation,
    useDeleteAvatarMutation,
    useUpdateBaseProfileDataMutation,
    useUpdateLocationDataMutation,
    useUpdateEmploymentDataMutation,
    useUpdateDetailsDataMutation,
    useUpdateLanguageMutation,
    useUpdateBankDataMutation,
    useUpdateSocialSecurityDataMutation,
    useUpdateContactPreferencesMutation,
    useChangePasswordMutation,
    useRequestProfileDeletionMutation,
    useWithdrawProfileDeletionMutation,
    usePostSendSmsCodeMutation,
    useVerifySmsCodeMutation,
    useDeleteAuthMethodMutation,
    useConnectExternalAuthMethodMutation,
    useUploadFileMutation,
    useDeleteFileMutation,
    useCreateWorkExperienceMutation,
    useUpdateWorkExperienceMutation,
    useDeleteWorkExperienceMutation,
    useCreateEducationMutation,
    useUpdateEducationMutation,
    useDeleteEducationMutation,
    useCreateProjectExperienceMutation,
    useUpdateProjectExperienceMutation,
    useDeleteProjectExperienceMutation,
    useCreateCourseMutation,
    useUpdateCourseMutation,
    useUploadCourseFileMutation,
    useDeleteCourseFileMutation,
    useDeleteCourseMutation,
    useCreateReferenceMutation,
    useUpdateReferenceMutation,
    useDeleteReferenceMutation,
    useCreateRelativeMutation,
    useUpdateRelativeMutation,
    useDeleteRelativeMutation,
    useCreateSkillMutation,
    useUpdateSkillMutation,
    useDeleteSkillMutation,
    useBatchSkillMutation,
    useCreateLanguageMutation,
    useUpdateLanguageItemMutation,
    useDeleteLanguageItemMutation,
    useUpdateLicencesMutation,
    useUploadCvMutation,
    useDeleteProfileFileMutation,
    useConnectCvParsingSocketMutation,
    useTriggerCvParsingMutation,
    useUpdateDescriptionMutation,
} = newProfileApi